import {
  CloseCircleOutlined,
  ReloadOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { css } from '@emotion/react';
import {
  Button,
  Col,
  Collapse,
  CollapseProps,
  DatePicker,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Typography
} from 'antd';

import {
  getNotificationStatusOptions,
  getReceiverOptions,
  convertTargetGroupsToOptions
} from '@/constants/notification.constant';
import { useAppSelector } from '@/redux/hooks';
import { watchTargetGroups } from '@/redux/slices/notification.slice';
import { useState } from 'react';
import dayjs from 'dayjs';

type FoodSliderSearcherProps = {
  type: 'EMAIL' | 'WEBAPP';
  form: FormInstance<FilterNotification>;
  onSearch: (values: FilterNotification) => void;
};

export const NotificationManagementSearcher = ({
  form,
  type,
  onSearch
}: FoodSliderSearcherProps) => {
  const [targetGroupValue, setTargetGroupValue] = useState<number[]>([]);
  const targetGroups = useAppSelector(watchTargetGroups);

  const handleSearch = () => {
    const values = form.getFieldsValue();
    const formattedValues: FilterNotification = {
      ...values,
      TYPE: type === 'EMAIL' ? 'EMAIL' : 'WEB/APP'
    };

    if (values.UPDATE_DATE_START) {
      formattedValues.UPDATE_DATE_START = dayjs(values.UPDATE_DATE_START).format('YYYY-MM-DD');
    }
    if (values.UPDATE_DATE_END) {
      formattedValues.UPDATE_DATE_END = dayjs(values.UPDATE_DATE_END).format('YYYY-MM-DD');
    }

    onSearch(formattedValues);
  };

  const targetGroupOptions = convertTargetGroupsToOptions(targetGroups);

  const handleClear = () => {
    form.resetFields();
    setTargetGroupValue([]);
    onSearch({
      TYPE: type === 'EMAIL' ? 'EMAIL' : 'WEB/APP'
    });
  };

  const statusOptions = getNotificationStatusOptions();
  const receiverOptions = getReceiverOptions();

  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: (
        <Typography.Title css={titleStyles} level={5}>
          BỘ LỌC
        </Typography.Title>
      ),
      children: (
        <Form form={form} css={formStyles}>
          <Row gutter={16} align='stretch'>
            <Col span={8} css={filterItem}>
              <Form.Item
                labelCol={{ span: 12 }}
                wrapperCol={{ span: 12 }}
                label={`Tìm kiếm theo tiêu đề${type === 'EMAIL' ? ' email' : ''}`}
                name='TITLE'
                css={customLabel}
              >
                <Input placeholder='Nhập từ khoá' allowClear />
              </Form.Item>
            </Col>
            <Col span={6} css={filterItem}>
              <Form.Item
                labelCol={{ span: 10 }}
                wrapperCol={{ span: 14 }}
                label='Trạng thái gửi'
                name='STATUS'
                css={customLabel}
              >
                <Select
                  allowClear
                  options={statusOptions}
                  placeholder='Chọn trạng thái gửi'
                />
              </Form.Item>
            </Col>
            <Col span={10} css={filterItem}>
              <Form.Item
                label='Thời gian cập nhật'
                style={{ width: '100%' }}
                css={customLabel}
                labelCol={{ span: 8 }}
                colon={false}
              >
                <Row
                  gutter={8}
                  style={{
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Col span={11}>
                    <Form.Item
                      name='UPDATE_DATE_START'
                      style={{ width: '100%' }}
                    >
                      <DatePicker
                        format='DD/MM/YYYY'
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={2} style={{ textAlign: 'center' }}>
                    {' '}
                    -{' '}
                  </Col>
                  <Col span={11}>
                    <Form.Item name='UPDATE_DATE_END' style={{ width: '100%' }}>
                      <DatePicker
                        format='DD/MM/YYYY'
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16} align='middle'>
            <Col span={8} css={filterItem}>
              <Form.Item
                name='TARGET_TYPE'
                label='Người nhận'
                labelCol={{ span: 12 }}
                wrapperCol={{ span: 12 }}
                css={customLabel}
              >
                <Select
                  allowClear
                  placeholder='Chọn người nhận'
                  options={receiverOptions}
                  onChange={(value) => {
                    if (value === 'ALL' || !value) {
                      setTargetGroupValue([]);
                      form.setFieldsValue({ TARGET_GROUP: [] });
                    }
                  }}
                />
              </Form.Item>
            </Col>

            <Col span={9} css={filterItem}>
              <Form.Item noStyle shouldUpdate>
                {(form) => {
                  const receiverValue = form.getFieldValue('TARGET_TYPE');

                  if (receiverValue === 'ADJUST')
                    return (
                      <Form.Item
                        name='TARGET_GROUP'
                        label='Trạng thái sức khỏe'
                        css={customLabel}
                        labelCol={{ span: 10 }}
                        wrapperCol={{ span: 14 }}
                      >
                        <div
                          css={
                            targetGroupValue.length > 0
                              ? null
                              : ContainerSelectHealthStatus
                          }
                        >
                          <Select
                            css={selectStyleHealthStatus}
                            mode='multiple'
                            allowClear
                            options={targetGroupOptions}
                            placeholder='Chọn trạng thái sức khỏe'
                            style={{ width: '95%' }}
                            value={targetGroupValue}
                            onChange={(value) => {
                              setTargetGroupValue(value || []);
                              form.setFieldsValue({ TARGET_GROUP: value });
                            }}
                            removeIcon={
                              <CloseCircleOutlined
                                style={{ color: '#1890FF', fontSize: '1.9rem' }}
                              />
                            }
                          />
                        </div>
                      </Form.Item>
                    );
                }}
              </Form.Item>
            </Col>
            <Col span={7}></Col>
          </Row>

          <Row
            gutter={16}
            align='middle'
            style={{
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <Button
              type='default'
              icon={<ReloadOutlined />}
              css={resetButton}
              onClick={handleClear}
            >
              Đặt lại
            </Button>
            <Button
              type='primary'
              icon={<SearchOutlined />}
              css={searchButton}
              onClick={handleSearch}
            >
              Tìm kiếm
            </Button>
          </Row>
        </Form>
      )
    }
  ];
  return (
    <Row css={boxStyles} justify='space-between' align='middle'>
      <Collapse
        items={items}
        defaultActiveKey={'1'}
        expandIconPosition={'end'}
        css={collapseStyles}
      />
    </Row>
  );
};

const boxStyles = css`
  padding: 0 1rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
`;

const collapseStyles = css`
  width: 100%;
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;
  background-color: transparent !important;

  .ant-collapse-header {
    width: fit-content;
    border-radius: 0.8rem !important;
    border: none !important;
    padding: 0 !important;

    .ant-collapse-expand-icon {
      align-self: center;
    }
  }

  .ant-collapse-content-box {
    padding: 0 !important;
  }

  .ant-collapse-content {
    border: none !important;
  }
`;

const titleStyles = css`
  margin-bottom: 0 !important;
  line-height: 3.2rem !important;
  font-weight: 700 !important;
`;

const formStyles = css`
  margin-top: 1rem;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem; /* Khoảng cách đồng đều giữa các rows */
  .ant-form-item-label {
      display: flex;
      align-items: center;
    }
`;

const filterItem = css`
  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-form-item-label {
    text-align: start !important;
    white-space: normal;
    font-size: 14px;
    font-weight: 500;
    word-wrap: break-word;
    word-break: break-word;
  }

  .ant-form-item-label > label {
    height: auto !important;
  }

  input,
  .ant-select,
  .ant-picker {
    width: 100%;
    font-size: 1.2rem !important;
  }

  .ant-select-selector {
    .ant-select-selection-placeholder {
      font-size: 1.2rem;
    }
  }

  .ant-picker-input input::placeholder {
    font-size: 1.2rem !important;
  }
`;

const customLabel = css`
  .ant-form-item-label > label {
    font-weight: normal !important;

    &::after,
    &::before {
      display: none !important;
    }
  }

  width: 100%;
  margin-bottom: 0;
`;

const resetButton = css`
  margin-right: 10px;
  color: #007bff;
  border-color: #007bff;
`;

const searchButton = css`
  background: #007bff;
  color: white;
  &:hover {
    background: #0056b3;
  }
`;

const ContainerSelectHealthStatus = css`
  border: 1px solid #00000040;
  border-radius: 0.5rem;
  padding: 0.8rem 1.6rem;

  .ant-select-selection-item {
    background: #ffffff !important;
    border-radius: 0.2rem !important;
    border: 1px solid #d9d9d9 !important;

    .ant-select-selection-item-content {
    }
  }
`;

const selectStyleHealthStatus = css`
  .ant-select-selection-item {
    background: #ffffff !important;
    border-radius: 0.2rem !important;
    border: 1px solid #d9d9d9 !important;

    .ant-select-selection-item-content {
      font-size: 1.2rem !important;
    }
  }
`;
